import type { FormAnswers, FormDefinition } from '@serviceportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ClipboardCheck, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { FormRenderer } from '@/components/form-renderer';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, Select } from '@/components/ui/primitives';
import { api, ApiError, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { FormItem, FormSubmissionItem, Paginated } from '@/lib/types';
import { formatDateTime } from '@/lib/utils';
import { SubmissionAnswers } from '@/pages/forms/forms-page';

/** Interne Checklisten (Formulare der Art „Checkliste“) zu einem Ticket oder einer Reparatur. */
export function ChecklistsCard({ ticketId, repairId }: { ticketId?: string; repairId?: string }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [formId, setFormId] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const ctxQuery = ticketId ? `ticketId=${ticketId}` : `repairId=${repairId}`;
  const enabled = can('forms.read');

  const { data: forms } = useQuery({
    queryKey: ['forms', 'checklists'],
    queryFn: () => api<FormItem[]>('/forms?kind=CHECKLIST'),
    enabled,
  });
  const available = forms?.filter((f) => f.active && f.publishedVersion) ?? [];
  const { data: subs } = useQuery({
    queryKey: ['forms', 'submissions', ctxQuery],
    queryFn: () => api<Paginated<FormSubmissionItem>>(`/forms/submissions?${ctxQuery}&pageSize=50`),
    enabled,
  });
  const { data: published } = useQuery({
    queryKey: ['forms', 'published', formId],
    queryFn: () => api<{ id: string; name: string; definition: FormDefinition }>(`/forms/${formId}/published`),
    enabled: Boolean(formId),
  });

  const submit = useMutation({
    mutationFn: (data: FormAnswers) => api(`/forms/${formId}/submit`, { method: 'POST', body: { data, ticketId: ticketId ?? null, repairId: repairId ?? null } }),
    onSuccess: () => {
      toast.success('Checkliste gespeichert');
      setOpen(false);
      setFormId('');
      qc.invalidateQueries({ queryKey: ['forms', 'submissions', ctxQuery] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['repairs'] });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.details) setServerErrors(Object.fromEntries(e.details.map((d) => [d.path, d.message])));
      toast.error(errorMessage(e));
    },
  });

  if (!enabled || (available.length === 0 && !subs?.items.length)) return null;

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-1.5 text-sm">
          <ClipboardCheck className="size-4" /> Checklisten
        </CardTitle>
        {available.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => { setOpen(true); setFormId(available.length === 1 ? available[0]!.id : ''); }}>
            <Plus /> Ausfüllen
          </Button>
        )}
      </CardHeader>
      <CardContent className="grid gap-2">
        {subs?.items.length === 0 && <p className="text-xs text-muted-foreground">Noch keine Checkliste ausgefüllt.</p>}
        {subs?.items.map((s) => (
          <div key={s.id} className="rounded-md border">
            <button className="flex w-full items-center gap-2 p-2 text-left text-sm" onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
              {expanded === s.id ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
              <span className="flex-1 truncate font-medium">{s.formName}</span>
              <span className="text-xs text-muted-foreground">
                {s.submitterName} · {formatDateTime(s.createdAt)}
              </span>
            </button>
            {expanded === s.id && (
              <div className="border-t p-3">
                <SubmissionAnswers submission={s} />
              </div>
            )}
          </div>
        ))}
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent wide>
          <DialogHeader>
            <DialogTitle>Checkliste ausfüllen</DialogTitle>
            <DialogDescription>Wird mit Ihrem Namen und Zeitstempel am Vorgang gespeichert.</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            {available.length > 1 && (
              <Select value={formId} onChange={(e) => setFormId(e.target.value)} aria-label="Checkliste">
                <option value="">Checkliste wählen …</option>
                {available.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </Select>
            )}
            {published && published.id === formId && (
              <FormRenderer
                key={formId}
                definition={published.definition}
                submitting={submit.isPending}
                serverErrors={serverErrors}
                onSubmit={async (data) => {
                  setServerErrors({});
                  await submit.mutateAsync(data).catch(() => undefined);
                }}
                uploadFile={async (file) => {
                  const fd = new window.FormData();
                  fd.append('file', file);
                  return api<{ token: string; name: string }>(`/forms/${formId}/files`, { method: 'POST', form: fd });
                }}
              />
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
